import { router } from "@inertiajs/react"
import { MoreHorizontal } from "lucide-react"
import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { adminAgentAgentChangelogEntryPath } from "@/routes"
import type { Agent, AgentChangelogEntry } from "@/types"

import EditAgentEntry from "./edit-agent-entry"

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  const month = date.toLocaleDateString("en-US", { month: "short" })
  const day = date.getDate()
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
  return `${month} ${day}, ${time}`
}

interface AdminAgentChangelogProps {
  entries: AgentChangelogEntry[]
  agent: Agent
}

export default function AdminAgentChangelog({
  entries,
  agent,
}: AdminAgentChangelogProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [selectedEntry, setSelectedEntry] =
    useState<AgentChangelogEntry | null>(null)
  const [processing, setProcessing] = useState(false)

  const handleEditClick = (entry: AgentChangelogEntry) => {
    setSelectedEntry(entry)
    setShowEditDialog(true)
  }

  const handleDeleteClick = (entry: AgentChangelogEntry) => {
    setSelectedEntry(entry)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = () => {
    if (!selectedEntry) return

    setProcessing(true)
    router.delete(
      adminAgentAgentChangelogEntryPath(agent.uid, selectedEntry.uid),
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          setShowDeleteDialog(false)
          setSelectedEntry(null)
          router.reload({ only: ["agent"] })
        },
        onError: () => {
          setProcessing(false)
        },
        onFinish: () => {
          setProcessing(false)
        },
      },
    )
  }
  if (!entries || entries.length === 0) {
    return <div className="text-muted-foreground text-sm">No updates yet.</div>
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="bg-border absolute top-0 bottom-0 left-[0.20rem] w-0.5" />

      <div className="space-y-6">
        {entries.map((entry) => (
          <div key={entry.uid} className="relative flex gap-4">
            {/* Timeline dot */}
            <div className="relative left-1 z-10 flex h-2.5 w-2.5 -translate-x-1/2 items-center justify-center">
              <div className="bg-border h-2.5 w-2.5 rounded-full" />
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    {formatDateTime(entry.created_at)}
                  </div>
                  <div className="mb-1 font-semibold">{entry.title}</div>
                  <div className="text-muted-foreground text-sm">
                    {entry.body}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">More options</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => handleEditClick(entry)}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => handleDeleteClick(entry)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              update &quot;{selectedEntry?.title}&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90 text-white"
              disabled={processing}
            >
              {processing ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditAgentEntry
        agent={agent}
        entry={selectedEntry}
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open)
          if (!open) {
            setSelectedEntry(null)
          }
        }}
      />
    </div>
  )
}
